// Field definitions for the signal / binding / codec model built in
// 00117-starek-324-nwm's main/signals/ (binding_json.cpp parses these exact
// wire keys - see signals/wire.h) - shared, plain-data module so
// DeviceEditSidebar.jsx (Tailwind) and AddDeviceProfileWizard.jsx (Bootstrap)
// can each render their own styled form from the same schema instead of
// forcing one shared component across two different CSS approaches.
//
// Replaces the retired analyzerSchema.js (still readable in git history) -
// the wire shape moved from one flat "analyzer" object to a {binding, codec}
// pair (see 00117-starek-backend-v2's utils/signalWire.ts, which this
// mirrors). A signal authored under the old shape still loads and decodes
// fine (the backend upgrades it on read) - only the editing form changed.

import { decodeGpioType, FALLBACK_DEVICE_FLAGS } from "./gpioTypeUtils";

// Only the two axes deriveAccess actually cares about - decodeGpioType
// gates which axes it decodes by manifestParams' own declared names, so this
// stands in for a manifest entry without needing one.
const DIRECT_ACCESS_AXES = [{ name: "actuateOrSense" }, { name: "controlOrState" }];

// Every protocol a signal's binding can use. "direct" is the only option on
// a GPIO/virtual source (a new signal there just re-scales that port's own
// value under a new name/codec - the old analyzer model's most common case);
// /uart and /i2c offer the richer choices below instead.
export const PROTOCOLS = [
    { value: "direct", label: "Direct (this port's own value, rescaled)" },
    { value: "i2c", label: "I2C register" },
    { value: "modbus_rtu", label: "Modbus RTU" },
    { value: "raw_frame", label: "Raw frame (passive listener)" },
];

export const MODBUS_TABLES = [
    { value: "holding_register", label: "Holding register (read/write)" },
    { value: "coil", label: "Coil (read/write)" },
    { value: "input_register", label: "Input register (read-only)" },
    { value: "discrete_input", label: "Discrete input (read-only)" },
];

export const CODEC_KINDS = [
    { value: "uint", label: "Unsigned integer" },
    { value: "sint", label: "Signed integer" },
    { value: "float32", label: "32-bit float" },
    { value: "bits", label: "Bit field (read-only)" },
];

export const FAILSAFE_MODES = [
    { value: "hold", label: "Hold last value (no failsafe)" },
    { value: "zero", label: "Revert to 0 / off" },
    { value: "value", label: "Revert to a fixed value" },
];

// Which protocol(s) a source can use, when the device hasn't reported
// capabilities yet (see deviceProtocolOptions below for the capability-aware
// version) - mirrors the backend's fallbackProtocols in utils/signalWire.ts.
export function protocolsForSource(source) {
    if (source === "/uart") return ["modbus_rtu", "raw_frame"];
    if (source === "/i2c") return ["i2c"];
    return ["direct"]; // any GPIO port or /virt/* signal
}

// Bit N of an endpoint's capability mask -> protocol name, matching
// SIGNAL_WIRE.PROTO (Device.WIRE.PROTO, served on /_types). Static here
// rather than sourced from `wire` at call sites, since only the id numbers
// (not the human labels) are ever bit-tested.
const PROTOCOL_BIT = { direct: 0, i2c: 1, modbus_rtu: 2, raw_frame: 3 };

// PROTOCOLS filtered to what this device's own reported capabilities allow
// for `source`, falling back to the stock rule for a device that hasn't
// latched with capabilities yet (older firmware, or a device profile being
// authored with no live device attached).
export function deviceProtocolOptions(source, capabilities) {
    const mask = capabilities?.ep?.[source];
    const allowed =
        typeof mask === "number"
            ? PROTOCOLS.filter((p) => (mask & (1 << PROTOCOL_BIT[p.value])) !== 0)
            : PROTOCOLS.filter((p) => protocolsForSource(source).includes(p.value));
    return allowed.length > 0 ? allowed : PROTOCOLS.filter((p) => protocolsForSource(source).includes(p.value));
}

// CODEC_KINDS filtered to what this device's firmware implements (`capabilities.k`,
// a bit mask keyed the same way as Device.WIRE.CODEC kind ids) - every kind
// when the device hasn't reported one.
const CODEC_KIND_BIT = { uint: 1, sint: 2, float32: 3, bits: 4 };
export function deviceCodecKindOptions(capabilities) {
    const mask = capabilities?.k;
    if (typeof mask !== "number") return CODEC_KINDS;
    const allowed = CODEC_KINDS.filter((k) => (mask & (1 << CODEC_KIND_BIT[k.value])) !== 0);
    return allowed.length > 0 ? allowed : CODEC_KINDS;
}

// Binding fields, by protocol - what WHERE a signal lives on its source.
// Access (read/write) is never one of these: it's derived from protocol +
// table + codec (see the UI's deriveAccess, mirroring the backend's).
export const PROTOCOL_FIELDS = {
    direct: [],
    i2c: [
        { name: "unit", label: "I2C address", type: "number", default: 0 },
        { name: "address", label: "Register", type: "number", default: 0 },
    ],
    modbus_rtu: [
        { name: "unit", label: "Slave ID", type: "number", default: 1 },
        {
            name: "table",
            label: "Table",
            type: "select",
            options: MODBUS_TABLES,
            default: "holding_register",
        },
        { name: "address", label: "Register / coil address", type: "number", default: 0 },
    ],
    raw_frame: [
        { name: "frame_start", label: "Start delimiter (byte value, optional)", type: "number", default: null },
        { name: "frame_end", label: "End delimiter (byte value)", type: "number", default: 10 },
        { name: "frame_length", label: "Fixed length (used if no end delimiter)", type: "number", default: 0 },
    ],
};

// Codec fields, shared by every protocol except raw_frame's own field-
// extraction stage below (which nests these same fields).
const CODEC_BASE_FIELDS = [
    {
        name: "kind",
        label: "Value type",
        type: "select",
        options: CODEC_KINDS,
        default: "uint",
    },
    { name: "width", label: "Width (bytes)", type: "number", default: 2 },
    {
        name: "byte_order",
        label: "Byte order",
        type: "select",
        options: [
            { value: "big", label: "Big-endian" },
            { value: "little", label: "Little-endian" },
        ],
        // Big-endian by default for i2c/modbus/raw_frame - matches how most
        // external wire protocols lay out a multi-byte field. A direct
        // (GPIO/virtual) signal's own default binding is the one exception
        // (little-endian, native uint16) and isn't user-authored, so it
        // never goes through this form.
        default: "big",
    },
    {
        name: "word_swap",
        label: "Swap 16-bit words (ABCD vs CDAB)",
        type: "checkbox",
        default: false,
        // Only meaningful for a 4-byte value - hidden by the form when
        // width !== 4 (see fieldVisible below).
        showWhen: (values) => Number(values.width) === 4,
    },
    { name: "offset", label: "Offset (bytes)", type: "number", default: 0 },
    { name: "scale", label: "Scale", type: "number", default: 1 },
    { name: "bias", label: "Bias", type: "number", default: 0 },
    {
        name: "mask",
        label: "Bitmask (decimal)",
        type: "number",
        default: 0xffff,
        showWhen: (values) => values.kind === "bits",
    },
];

export const CODEC_FIELDS = CODEC_BASE_FIELDS;

// Fields for a writable binding's failsafe: reverts an output if nothing
// re-commands it within `ttl` seconds. Shown only when the chosen protocol/
// table combination is actually writable (see deriveAccess) - offering a
// failsafe on a read-only signal is meaningless.
export const FAILSAFE_FIELDS = [
    {
        name: "failsafe_mode",
        label: "Failsafe",
        type: "select",
        options: FAILSAFE_MODES,
        default: "hold",
    },
    {
        name: "failsafe_value",
        label: "Failsafe value",
        type: "number",
        default: 0,
        showWhen: (values) => values.failsafe_mode === "value",
    },
    {
        name: "failsafe_ttl",
        label: "Failsafe TTL (seconds)",
        type: "number",
        default: 30,
        showWhen: (values) => values.failsafe_mode !== "hold",
    },
];

// True for a table that's inherently read-only regardless of codec (mirrors
// the backend's isBitTable / a coil's own read/write pair).
function isReadOnlyTable(table) {
    return table === "discrete_input" || table === "input_register";
}

// Whether a {protocol, table, codecKind, readOnly} combination can be read
// and/or written - the same derivation the backend and firmware both apply,
// kept here only so the form can hide/gray out write-only affordances (the
// failsafe fields) and label a GPIO signal's single config correctly before
// the user ever saves; the backend re-validates and the firmware re-checks
// and reports on /status regardless. `physicalType` is the source endpoint's
// own type bitmap when it's known (a live device); left undefined (no live
// device to ask, e.g. the profile wizard) both read and write default to
// true, same permissive fallback the backend uses. For "direct" (GPIO),
// read/write are the port's own Sense/Actuate axis (Control additionally
// grants write on top of Sense - a channel you can both read back and set;
// State never grants write beyond what Actuate already does) - never both
// unconditionally true the way every other protocol's `read` is, since a
// write-only Actuate port genuinely cannot be read as this signal.
export function deriveAccess({
    protocol,
    table,
    codecKind,
    readOnly,
    physicalType,
    deviceFlags = FALLBACK_DEVICE_FLAGS,
}) {
    if (readOnly) return { read: true, write: false };
    switch (protocol) {
        case "direct": {
            if (physicalType === undefined) return { read: true, write: true };
            const decoded = decodeGpioType(physicalType, deviceFlags, DIRECT_ACCESS_AXES);
            const isActuate = decoded.actuateOrSense === "Actuate";
            const isSense = decoded.actuateOrSense === "Sense";
            const isControl = decoded.controlOrState === "Control";
            return {
                read: isSense || isControl,
                write: isActuate || isControl,
            };
        }
        case "i2c":
            return { read: true, write: codecKind !== "bits" };
        case "modbus_rtu":
            if (isReadOnlyTable(table)) return { read: true, write: false };
            return {
                read: true,
                write: table === "coil" || codecKind !== "bits",
            };
        default: // raw_frame: a passive listener never transmits
            return { read: true, write: false };
    }
}

// One flat {kind, width, byte_order, ...} config a create/edit form can bind
// straight to controlled inputs, given a chosen protocol. Distinct from the
// wire shape (see SignalControls/DeviceEditSidebar's own save-time mapping
// into {binding, codec}) - this is only the form's own working state.
export function defaultSignalConfig(protocol) {
    const protocolFields = PROTOCOL_FIELDS[protocol] || [];
    const config = { protocol };
    for (const f of protocolFields) config[f.name] = f.default;
    for (const f of CODEC_FIELDS) config[f.name] = f.default;
    for (const f of FAILSAFE_FIELDS) config[f.name] = f.default;
    // A direct binding's raw value is fed as a native little-endian uint16
    // (Xtensa) - every other protocol's default (big-endian) matches how
    // most external wire protocols lay a multi-byte field out instead.
    if (protocol === "direct") config.byte_order = "little";
    return config;
}

// Splits a form's flat working state back into the {binding, codec} pair
// SignalEntry expects (see 00117-starek-backend-v2's utils/signalWire.ts) -
// the save-time counterpart of defaultSignalConfig/formValuesFromEntry.
export function entryFromFormValues(values) {
    const { protocol, kind, width, byte_order, word_swap, offset, scale, bias, mask, ...rest } = values;
    const binding = { protocol };
    if (rest.table !== undefined) binding.table = rest.table;
    if (rest.unit !== undefined) binding.unit = Number(rest.unit);
    if (rest.address !== undefined) binding.address = Number(rest.address);
    if (protocol === "raw_frame") {
        binding.frame = {
            start: rest.frame_start === null || rest.frame_start === "" ? undefined : Number(rest.frame_start),
            end: rest.frame_end === null || rest.frame_end === "" ? undefined : Number(rest.frame_end),
            length: Number(rest.frame_length) || 0,
        };
    }
    if (rest.failsafe_mode && rest.failsafe_mode !== "hold") {
        binding.failsafe = {
            mode: rest.failsafe_mode,
            ttl: Number(rest.failsafe_ttl) || 0,
            ...(rest.failsafe_mode === "value" && { value: Number(rest.failsafe_value) || 0 }),
        };
    }

    const codec = {
        kind,
        width: Number(width),
        byte_order,
        ...(Number(width) === 4 && word_swap && { word_swap: true }),
        offset: Number(offset) || 0,
        scale: scale === undefined || scale === "" ? 1 : Number(scale),
        bias: bias === undefined || bias === "" ? 0 : Number(bias),
        ...(kind === "bits" && { mask: Number(mask) }),
    };

    return { binding, codec };
}

// The inverse of entryFromFormValues - loads an existing signal (already
// normalized to {binding, codec} by the backend, even one authored under the
// old analyzer shape) into the form's flat working state.
export function formValuesFromEntry(entry) {
    const b = entry?.binding || {};
    const c = entry?.codec || {};
    const values = defaultSignalConfig(b.protocol || "i2c");
    Object.assign(values, {
        protocol: b.protocol,
        table: b.table,
        unit: b.unit,
        address: b.address,
        frame_start: b.frame?.start ?? null,
        frame_end: b.frame?.end ?? 10,
        frame_length: b.frame?.length ?? 0,
        failsafe_mode: b.failsafe?.mode || "hold",
        failsafe_value: b.failsafe?.value ?? 0,
        failsafe_ttl: b.failsafe?.ttl ?? 30,
        kind: c.kind || "uint",
        width: c.width ?? 2,
        byte_order: c.byte_order || "big",
        word_swap: !!c.word_swap,
        offset: c.offset ?? 0,
        scale: c.scale ?? 1,
        bias: c.bias ?? 0,
        mask: c.mask ?? 0xffff,
    });
    return values;
}

// Fields to actually render for the current form state - a protocol's own
// binding fields, then the codec fields, then failsafe fields when the
// combination is writable. Each field's own `showWhen` (if any) further
// hides it (e.g. word_swap only at width 4).
export function visibleFields(values, physicalType, deviceFlags) {
    const protocolFields = PROTOCOL_FIELDS[values.protocol] || [];
    const access = deriveAccess({
        protocol: values.protocol,
        table: values.table,
        codecKind: values.kind,
        physicalType,
        ...(deviceFlags && { deviceFlags }),
    });
    const fields = [...protocolFields, ...CODEC_FIELDS];
    if (access.write) fields.push(...FAILSAFE_FIELDS);
    return fields.filter((f) => !f.showWhen || f.showWhen(values));
}
